import React, {Suspense, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useMileage, {ChallengeType} from '@di-shared/features/mileage/useMileage';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HomeMileageInnerContentsEcoKrProps {
  vin: string;
}

function HomeMileageInnerContentsEcoKr({vin}: HomeMileageInnerContentsEcoKrProps): JSX.Element {
  return (
    <ErrorBoundary fallback={<HomeMileageInnerContentsEcoError />}>
      <Suspense fallback={<HomeMileageInnerContentsEcoLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: HomeMileageInnerContentsEcoKrProps): JSX.Element {
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.getInfoLink), vin],
    queryFn: () => diApi.ecoMileageController.getInfoLink({vin}),
  });

  if (data.data.rtn_data.drvng_stt_cd === '022') {
    // 구제도 도전중
    return <HomeMileageInnerContentsEcoKrV1 vin={vin} />;
  }
  if (data.data.rtn_data.drvng_stt_cd === '063') {
    // 신제도 도전중
    return <HomeMileageInnerContentsEcoV2Kr vin={vin} />;
  }
  return <HomeMileageInnerContentsEcoUpdate />;
}

function HomeMileageInnerContentsEcoKrV1({vin}: HomeMileageInnerContentsEcoKrProps): JSX.Element {
  const {t} = useInferableTranslation();
  const {getChallengeType} = useMileage(vin);
  const {odometer} = useOdometer(vin);
  const challengeType = getChallengeType(odometer);

  function getMileageAmountText(challengeType: ChallengeType): string {
    switch (challengeType) {
      case ChallengeType.SEVEN:
        return t('T10125_$mileage', {mileage: 7});
      case ChallengeType.FIVE:
        return t('T10125_$mileage', {mileage: 5});
      case ChallengeType.TRREE:
        return t('T10125_$mileage', {mileage: 3});
      case ChallengeType.TWO:
        return t('T10125_$mileage', {mileage: 2});
      case ChallengeType.UPDATE_STATUS:
        return t('T10133');
      case ChallengeType.CHALLENGE_UNAVAILABLE:
      default:
        return '';
    }
  }

  return (
    <li className="eco_state_item">
      <strong className="eco_state_title">{t('T10124')}</strong>
      <p
        className="eco_state_desc"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {getMileageAmountText(challengeType)}
      </p>
      {challengeType === ChallengeType.CHALLENGE_UNAVAILABLE ? (
        <div className="badge_box">
          <span className="badge badge_container_red badge_20px">{t('T10128')}</span>
        </div>
      ) : challengeType === ChallengeType.UPDATE_STATUS ? null : (
        <div className="badge_box">
          <span className="badge badge_basic_container badge_20px">{t('T10126')}</span>
        </div>
      )}
    </li>
  );
}

const challengeTypeKeys = ['01', '02', '03', '04', '05', '06'] as const;
type ChallengeTypeV2 = (typeof challengeTypeKeys)[number];
type ChallengeDateTypeV2 = Record<ChallengeTypeV2, {mileage: number; limitDistance: number}>;

export function HomeMileageInnerContentsEcoV2Kr({vin}: HomeMileageInnerContentsEcoKrProps): JSX.Element {
  const {t} = useInferableTranslation();
  const {data: calcLinkData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.calcLink), vin],
    queryFn: () => diApi.ecoMileageController.calcLink({vin}),
  });
  const challengeData = useMemo<ChallengeDateTypeV2>(
    () => ({
      // type1의 마일리지, type1의 제한주행거리
      '01': {
        mileage: calcLinkData.data.rtn_data.type1_mileage,
        limitDistance: calcLinkData.data.rtn_data.type1_dstnc,
      },
      // type2의 마일리지, type2의 제한주행거리
      '02': {
        mileage: calcLinkData.data.rtn_data.type2_mileage,
        limitDistance: calcLinkData.data.rtn_data.type2_dstnc,
      },
      '03': {
        mileage: calcLinkData.data.rtn_data.type3_mileage,
        limitDistance: calcLinkData.data.rtn_data.type3_dstnc,
      },
      '04': {
        mileage: calcLinkData.data.rtn_data.type4_mileage,
        limitDistance: calcLinkData.data.rtn_data.type4_dstnc,
      },
      '05': {
        mileage: calcLinkData.data.rtn_data.type5_mileage,
        limitDistance: calcLinkData.data.rtn_data.type5_dstnc,
      },
      // 06: 실패 케이스
      '06': {
        mileage: 0,
        limitDistance: 0,
      },
    }),
    [calcLinkData.data]
  );

  return (
    <li className="eco_state_item">
      <strong className="eco_state_title">{t('T10124')}</strong>
      <p
        className="eco_state_desc"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T10125_$mileage', {mileage: challengeData[calcLinkData.data.rtn_data.challenge_type as ChallengeTypeV2].mileage / 10000})}
      </p>
      {calcLinkData.data.rtn_data.challenge_type === '06' ? (
        <div className="badge_box">
          <span className="badge badge_container_red badge_20px">{t('T10128')}</span>
        </div>
      ) : (
        <div className="badge_box">
          <span className="badge badge_basic_container badge_20px">{t('T10126')}</span>
        </div>
      )}
    </li>
  );
}

export default HomeMileageInnerContentsEcoKr;

export function HomeMileageInnerContentsEcoError() {
  const {t} = useInferableTranslation();
  return (
    <li className="eco_state_item">
      <strong className="eco_state_title">{t('T10124')}</strong>
      <p className="eco_state_desc">{t('T10136')}</p>
      <div className="badge_box">
        <span className="badge badge_basic_container badge_20px">{t('T10126')}</span>
      </div>
    </li>
  );
}

function HomeMileageInnerContentsEcoUpdate() {
  const {t} = useInferableTranslation();
  return (
    <li className="eco_state_item">
      <strong className="eco_state_title">{t('T10124')}</strong>
      <p
        className="eco_state_desc"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T10133')}
      </p>
    </li>
  );
}

function HomeMileageInnerContentsEcoLoading() {
  const {t} = useInferableTranslation();
  return (
    <li className="eco_state_item skeleton">
      <strong className="eco_state_title">{t('T10124')}</strong>
      <p
        className="eco_state_desc"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T10133')}
      </p>
    </li>
  );
}
